import { Grid, Typography } from '@mui/material'
import { SelectedCloudIcon, ShortenedNameV2 } from '@labrav/common'
import { useStyles } from './ViewDetailsResellerAuthorization.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { useNavigate, useParams } from 'react-router-dom'
import {
  type MPPOResaleAuthStages,
  type ResaleAuthStages,
  type ResaleAuthorizationType,
} from '../../../modules/receivedResellerAuthorization/types'
import { ResellerAgreementStatus } from '../../../types/flyout'
import { ResellerAuthorizationDetails } from './ResellerAuthorizationDetails/ResellerAuthorizationDetails'
import clsx from 'clsx'
import { ViewRecipientsStakeholders } from './ViewRecipientsStakeholders/ViewRecipientsStakeholders'
import { ViewResellerAuthorizationEula } from './ViewResellerAuthorizationEula/ViewResellerAuthorizationEula'
import { DimensionDetails } from './DimensionDetails/DimensionDetails'
import { ResellerAuthorizationHeader } from './ResellerAuthorizationHeader/ResellerAuthorizationHeader'
import { StatusListing } from '@labrav/react-components'
import { ViewRecipients } from '../../../common/components/ViewRecipients/ViewRecipients'
import { ViewOfferEndBuyers } from '../../../common/components/ViewOfferEndBuyers/ViewOfferEndBuyers'
import { ViewStakeholders } from '../../../common/components/ViewStakeholders/ViewStakeholders'
import type { StatusOfferItem } from '../../../types/addEditProduct'
import {
  getMPPOResaleAuthStagesData,
  getResaleAuthStagesData,
} from '../../../utils/resaleAuthorizationUtil'
import { isEmpty } from 'lodash/fp'
import { useAccountId } from '@labrav/common'
import { LoadingTypes, isLoading as Loading } from '@labrav/common'
import type {
  OfferRecipients,
  OfferStakeholders,
} from '../../../modules/privateOffer/types'
import type { OfferEndBuyers } from '../../../types/flyout'
import { updateResaleAuthorizationForUser } from '../../PrivateOfferForm/Recipients/utils'
import { useDispatch, useSelector } from '../../../store'
import { useState } from 'react'
import { MicrosoftViewPricing } from '../../MicrosoftViewPricing'
import { EulaView } from '../../../common/components/EulaView/EulaView'
import { MicrosoftResellerAuthorizationDetails } from './ViewMicrosoftComponent/MicrosoftResellerAuthorizationDetails/MicrosoftResellerAuthorizationDetails'
import { CustomMetadataSection } from './CustomMetadataSection'
import { getResellerAuthorizationFields } from './helpers'
import type { ViewDetailsResellerAuthorizationProps } from '../types'

export const ViewDetailsResellerAuthorization = ({
  resellerAuthorization,
  crmOpportunityId,
  product,
  cloudMarketplace,
  currencySymbol,
  externalBaseURL,
  search,
  onBack,
  users = [],
  recipientStakeholderUsers = [],
}: ViewDetailsResellerAuthorizationProps) => {
  const { classes } = useStyles()
  const { accountId: routeAccountId, partnerId: routePartnerId } = useParams<{
    accountId: string
    partnerId: string
  }>()

  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || routePartnerId || fallbackAccountId || ''

  const navigate = useNavigate()
  const timeZone = useSelector(
    state =>
      state.userProfile.userProfile.timeZone ||
      state.userProfile.userProfile.defaultTimeZone ||
      'UTC'
  )
  const dispatch = useDispatch()

  const [openEditRecipient, setOpenEditRecipient] = useState(false)
  const [openEditStakeHolders, setOpenEditStakeholders] = useState(false)
  const [openEditOfferEndBuyers, setOpenEditOfferEndBuyers] = useState(false)

  const isUpdateLoading = useSelector(state =>
    Loading(state.loading)(LoadingTypes.UPDATE_RESALE_AUTHORIZATION)
  )

  const { agreementOrigin } = resellerAuthorization

  const isReceivedResellerAuth =
    agreementOrigin === ('RECEIVED' as ResaleAuthorizationType)

  const isChannelOfferAccepted =
    resellerAuthorization.status ===
    ResellerAgreementStatus.CHANNEL_OFFER_ACCEPTED
  const isAuthorizationCompleted =
    resellerAuthorization.status === ResellerAgreementStatus.COMPLETED ||
    isChannelOfferAccepted

  const auditJson: ResaleAuthStages = {
    'Authorization created':
      resellerAuthorization?.auditJson?.resaleAuthCreatedAt ?? '',
    'Authorization activated':
      resellerAuthorization?.auditJson?.resaleAuthActivatedAt ?? '',
    'Authorization completed':
      resellerAuthorization?.auditJson?.resaleAuthCompletedAt ||
      resellerAuthorization?.auditJson?.resaleAuthExpiredAt ||
      (isAuthorizationCompleted ? resellerAuthorization.updatedAt || '' : ''),
    'Channel offer accepted':
      resellerAuthorization?.auditJson?.resaleAuthChannelOfferAcceptedAt ||
      resellerAuthorization?.auditJson?.channelOfferAcceptedAt ||
      (isChannelOfferAccepted ? resellerAuthorization.updatedAt || '' : ''),
    'Authorization deactivated':
      resellerAuthorization?.auditJson?.resaleAuthDeactivatedAt ?? '',
    'Authorization creation failed':
      resellerAuthorization?.auditJson?.resaleAuthCreationFailedAt ?? '',
    'Authorization duration ended':
      resellerAuthorization?.auditJson?.resaleAuthExpiredAt ?? '',
  }

  const statusLogs: MPPOResaleAuthStages = {
    'Created at Labra':
      resellerAuthorization?.statusLogs?.offerCreationInProgress ?? '',
    'Sent to reseller':
      resellerAuthorization?.statusLogs?.offerSentReseller ?? '',
    'Viewed by reseller':
      resellerAuthorization?.statusLogs?.offerViewedReseller ?? '',
    'Extended to buyer':
      resellerAuthorization?.statusLogs?.offerExtendedToBuyer ?? '',
    'Pending purchase':
      resellerAuthorization?.statusLogs?.offerTermsAcceptedBuyer ?? '',
    'Pending account configuration':
      resellerAuthorization?.statusLogs?.offerPurchaseComplete ?? '',
    Accepted: resellerAuthorization?.statusLogs?.offerAccepted ?? '',
    Expired: resellerAuthorization?.statusLogs?.offerExpired ?? '',
    'Deactivation in progress':
      resellerAuthorization?.statusLogs?.offerRestrictionInProgress ?? '',
    Deactivated: resellerAuthorization?.statusLogs?.offerRestricted ?? '',
  }

  const statuses = () => {
    const stageDataArray = getResaleAuthStagesData(
      auditJson,
      resellerAuthorization.status,
      resellerAuthorization.agreementDurationType,
      resellerAuthorization.agreementOrigin ||
        ('CREATED' as ResaleAuthorizationType)
    )
    const filteredStageDataArray = stageDataArray
      ?.filter(obj => obj.display === true)
      ?.sort((a, b) => a.sortOrder - b.sortOrder)
    return filteredStageDataArray
  }

  const microsoftStatuses = () => {
    const stageDataArray = getMPPOResaleAuthStagesData(
      statusLogs,
      resellerAuthorization.offerStatus,
      resellerAuthorization?.statusLogs?.offerCreationFailed
    )
    const filteredStageDataArray = stageDataArray
      ?.filter(obj => obj.display === true)
      ?.sort((a, b) => (a?.sortOrder || 0) - (b?.sortOrder || 0))
    return filteredStageDataArray
  }

  const metaData = resellerAuthorization?.metaData || []
  const hasOfferMetaData = !!metaData.length

  const resellerAuthorizationFields = getResellerAuthorizationFields({
    cloudMarketplace,
    resellerAuthorization,
  })

  const handleSuccessSubmitStakeholder = () => setOpenEditStakeholders(false)

  const handleSubmitStakeholder = async (
    offerStakeholders: OfferStakeholders[],
    customOfferStakeholders: OfferStakeholders[]
  ) => {
    const payload = {
      offerStatus: resellerAuthorization?.offerStatus,
      offerStakeholders: [
        ...offerStakeholders.map(stakeholder => ({
          firstName: stakeholder.firstName || '',
          lastName: stakeholder.lastName || '',
          email: stakeholder.email,
        })),
        ...customOfferStakeholders.map(stakeholder => ({
          firstName: stakeholder.firstName || '',
          lastName: stakeholder.lastName || '',
          email: stakeholder.email,
        })),
      ],
    }

    await dispatch(
      updateResaleAuthorizationForUser(
        resellerAuthorization.id,
        payload,
        undefined,
        cloudMarketplace,
        false,
        handleSuccessSubmitStakeholder,
        undefined,
        undefined,
        accountId
      )
    )
  }

  const handleSuccessSubmitRecipient = () => setOpenEditRecipient(false)

  const handleSubmitRecipient = async (offerRecipients: OfferRecipients[]) => {
    const payload = {
      offerStatus: resellerAuthorization?.offerStatus,
      offerRecipients,
    }
    await dispatch(
      updateResaleAuthorizationForUser(
        resellerAuthorization.id,
        payload,
        undefined,
        cloudMarketplace,
        false,
        handleSuccessSubmitRecipient,
        undefined,
        undefined,
        accountId
      )
    )
  }

  const handleSuccessSubmitOfferEndBuyers = () =>
    setOpenEditOfferEndBuyers(false)
  const handleSubmitOfferEndBuyers = async (
    offerEndBuyers: OfferEndBuyers[]
  ) => {
    const payload = {
      offerStatus: resellerAuthorization?.offerStatus,
      offerEndBuyers: offerEndBuyers?.filter(
        (item: OfferEndBuyers) =>
          !!(item?.email || item?.firstName || item?.lastName)
      ),
    }
    await dispatch(
      updateResaleAuthorizationForUser(
        resellerAuthorization.id,
        payload,
        undefined,
        cloudMarketplace,
        false,
        handleSuccessSubmitOfferEndBuyers,
        undefined,
        undefined,
        accountId
      )
    )
  }
  const handleGoBack = () => {
    if (cloudMarketplace === 'AZURE') {
      navigate(`/flyout/azure/${accountId}/reseller-authorizations/sent`)
    } else {
      navigate(-1)
    }
  }
  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      handleGoBack()
    }
  }
  return (
    <div data-testid={'reseller-authorization'}>
      <div className={classes.headerContainer}>
        <div className={classes.appTitleHeading}>
          {(isEmpty(externalBaseURL) || onBack) && (
            <div
              className={classes.appBackIcon}
              data-testid="back-button"
              onClick={handleBack}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={{
                  width: '24px',
                  height: '24px',
                  fontSize: '24px',
                  padding: '0px 12px 0px 0px',
                  color: '#1942C5',
                }}
              />
            </div>
          )}
          <Typography
            variant="h1"
            data-testid="heading"
            className={classes.appTitleHeadingText}
          >
            <ShortenedNameV2
              name={resellerAuthorizationFields?.agreementName || ''}
              length={41}
            ></ShortenedNameV2>
          </Typography>
        </div>
        <SelectedCloudIcon />
      </div>
      <div className={classes.root}>
        <Grid container spacing={4}>
          <Grid
            className={classes.leftContainer}
            size={{
              sm: 12,
              md: 8,
              lg: 8,
              xl: 9,
            }}
          >
            <ResellerAuthorizationHeader
              resellerAuthorization={resellerAuthorization}
              crmOpportunityId={crmOpportunityId}
              externalBaseURL={externalBaseURL}
              search={search}
            />
            {cloudMarketplace === 'AWS' && (
              <>
                <ResellerAuthorizationDetails
                  resellerAuthorization={resellerAuthorization}
                  product={product}
                  currencySymbol={currencySymbol}
                />

                <DimensionDetails
                  resellerAuthorization={resellerAuthorization}
                  currencySymbol={currencySymbol}
                />
                <ViewResellerAuthorizationEula
                  resellerAuthorization={resellerAuthorization}
                />
              </>
            )}
            {cloudMarketplace === 'AZURE' && (
              <>
                <MicrosoftResellerAuthorizationDetails
                  resaleAuthorization={resellerAuthorization}
                />
                <MicrosoftViewPricing
                  plans={resellerAuthorization?.plans || []}
                  azurePricingType={resellerAuthorization?.azurePricingType}
                />
                <EulaView
                  eulaHeading={'ISV EULA for buyer'}
                  eulaFiles={resellerAuthorization?.eulaFiles || []}
                  eulaType={resellerAuthorization?.eulaType}
                />
              </>
            )}
            {hasOfferMetaData && <CustomMetadataSection metaData={metaData} />}
          </Grid>
          <Grid
            data-testId="resale-authorization-stages"
            className={clsx(
              classes.rightContainer,
              classes.stickyStatusListing
            )}
            size={{
              sm: 12,
              md: 4,
              lg: 4,
              xl: 3,
            }}
          >
            {cloudMarketplace === 'AWS' &&
              !isEmpty(resellerAuthorization?.auditJson) && (
                <StatusListing
                  statuses={statuses() as StatusOfferItem[]}
                  title={'Authorization stage'}
                  titleIcon={faListCheck}
                  timeZone={timeZone}
                  useBuiltinStyles={false}
                />
              )}

            {cloudMarketplace === 'AZURE' &&
              !isEmpty(resellerAuthorization?.statusLogs) && (
                <StatusListing
                  statuses={microsoftStatuses() as StatusOfferItem[]}
                  title={'Authorization stage'}
                  titleIcon={faListCheck}
                  timeZone={timeZone}
                  useBuiltinStyles={false}
                  isNextCompletedSupport
                />
              )}
            {cloudMarketplace === 'AZURE' && (
              <>
                <ViewRecipients
                  offerRecipients={resellerAuthorization?.offerRecipients || []}
                  handleSubmitRecipient={handleSubmitRecipient}
                  setOpenEditRecipient={setOpenEditRecipient}
                  openEditRecipient={openEditRecipient}
                  isUpdateLoading={isUpdateLoading}
                />
                <ViewOfferEndBuyers
                  offerEndBuyers={resellerAuthorization?.offerEndBuyers || []}
                  handleSubmitOfferEndBuyers={handleSubmitOfferEndBuyers}
                  setOpenEditOfferEndBuyers={setOpenEditOfferEndBuyers}
                  openEditOfferEndBuyers={openEditOfferEndBuyers}
                  isUpdateLoading={isUpdateLoading}
                />
                <ViewStakeholders
                  customOfferStakeholders={
                    resellerAuthorization?.customOfferStakeholders || []
                  }
                  offerStakeholders={
                    resellerAuthorization?.offerStakeholders || []
                  }
                  handleSubmitStakeholder={handleSubmitStakeholder}
                  setOpenEditStakeholders={setOpenEditStakeholders}
                  openEditStakeHolders={openEditStakeHolders}
                  isUpdateLoading={isUpdateLoading}
                  users={users}
                />
              </>
            )}
            {cloudMarketplace === 'AWS' && !isReceivedResellerAuth && (
              <ViewRecipientsStakeholders
                resellerAuthorization={resellerAuthorization}
                externalBaseURL={externalBaseURL}
                users={recipientStakeholderUsers}
              />
            )}
          </Grid>
        </Grid>
      </div>
    </div>
  )
}
